/** Keep the reader's visible content still while layout above it changes
 * height. Chat turns browser overflow anchoring off (it followed the footer
 * as replies streamed) and Safari never implemented it, so RN Web's estimated
 * spacers, late markdown/code layout, and images otherwise move the
 * transcript under the reader. Positions are measured within the content, so
 * scrolling never registers as drift; only layout does.
 * One instance belongs to one conversation.
 */
const MESSAGE_ROW_SELECTOR = '[id^="msg-hl-"]';

export function createChatScrollAnchor({ now = Date.now, selector = MESSAGE_ROW_SELECTOR } = {}) {
  let anchor = null;
  let pausedUntil = 0;

  const contentTopOf = (node, element, viewportTop) => (
    node.getBoundingClientRect().top - viewportTop + element.scrollTop
  );

  // Rows are in document order, so the first one still on screen can be
  // found by bisection instead of measuring every mounted row per event.
  const firstVisibleRow = (element, viewportTop) => {
    const rows = element.querySelectorAll?.(selector);
    if (!rows?.length) return null;
    let lo = 0;
    let hi = rows.length - 1;
    let found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (rows[mid].getBoundingClientRect().bottom > viewportTop + 1) {
        found = rows[mid];
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return found;
  };

  // A single answer can span many screens. Its wrapper stays put when lazy
  // math, fonts, or code above the paragraph being read finishes rendering.
  // Descend to visible content instead of anchoring that offscreen wrapper.
  // Stop at nested scrollers: their internal movement must not move the chat.
  const visibleContent = (row, element, viewportTop) => {
    let node = row;
    const viewportBottom = viewportTop + element.clientHeight;
    while (node) {
      if (node.scrollHeight > node.clientHeight + 1
        && /auto|scroll/.test(node.ownerDocument.defaultView?.getComputedStyle(node).overflowY || '')) break;
      const child = Array.from(node.children || []).find(candidate => {
        const rect = candidate.getBoundingClientRect();
        return rect.bottom > viewportTop + 1 && rect.top < viewportBottom && rect.bottom > rect.top;
      });
      if (!child) break;
      node = child;
    }
    return node;
  };

  /** Correct layout drift of the previous anchor when `adjust` is set, then
   * record the row now at the top. Returns the applied scroll delta. */
  const sync = (element, { adjust = false } = {}) => {
    if (!element?.isConnected || !(element.clientHeight > 0)) {
      anchor = null;
      return 0;
    }
    const viewportTop = element.getBoundingClientRect().top;
    let applied = 0;
    if (adjust && now() >= pausedUntil && anchor?.element === element && anchor.row.isConnected) {
      // Syntax highlighting/math can replace a descendant. The still-mounted
      // row remains a fallback for changes above the answer in that frame.
      const node = anchor.node.isConnected ? anchor.node : anchor.row;
      const baseline = node === anchor.node ? anchor.contentTop : anchor.rowContentTop;
      const drift = contentTopOf(node, element, viewportTop) - baseline;
      if (Math.abs(drift) >= 1) {
        const before = element.scrollTop;
        element.scrollTop = before + drift;
        applied = element.scrollTop - before;
      }
    }
    const row = firstVisibleRow(element, viewportTop);
    const node = row && visibleContent(row, element, viewportTop);
    anchor = node ? {
      element, row, node,
      contentTop: contentTopOf(node, element, viewportTop),
      rowContentTop: contentTopOf(row, element, viewportTop),
    } : null;
    return applied;
  };

  // Programmatic smooth scrolls (search, scroll-to-top) mount rows as they
  // pass; a correction mid-animation would cancel them.
  const pause = (ms = 1500) => {
    anchor = null;
    pausedUntil = now() + ms;
  };

  return { sync, pause };
}
